import { Injectable } from '@angular/core';
import { defer, finalize, Observable, shareReplay } from 'rxjs';

export interface EfileGridCacheEntry {
  data: any;
  timestamp: number;
}

/**
 * Caches grid API responses for efile screens so that revisiting a tab
 * does not trigger a new HTTP call. Keyed by a caller-supplied string
 * (typically the route path or action_id + grid_id composite).
 *
 * Mirrors `AdminGridCacheService` one-to-one so that the efile grid
 * directive has its own isolated cache namespace and cannot collide
 * with admin screens.
 */
@Injectable({ providedIn: 'root' })
export class EfileGridCacheService {
  private static readonly MAX_ENTRIES = 20;
  private cache = new Map<string, EfileGridCacheEntry>();
  private inFlightRequests = new Map<string, Observable<unknown>>();

  get(key: string, maxAgeMs?: number): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (maxAgeMs && Date.now() - entry.timestamp > maxAgeMs) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  set(key: string, data: any): void {
    if (this.cache.size >= EfileGridCacheService.MAX_ENTRIES && !this.cache.has(key)) {
      const oldest = this.cache.keys().next().value;
      if (oldest) this.cache.delete(oldest);
    }
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Coalesces identical in-flight grid reads.
   *
   * Return Review can replace a grid host while route state is settling. The
   * old and new directive instances may then request the same grid before the
   * first HTTP response arrives. Unsubscribing the old HttpClient subscriber
   * does not guarantee that the backend has not already received the POST, so
   * keep one shared request alive and let both instances consume its result.
   */
  loadOnce<T>(requestKey: string, requestFactory: () => Observable<T>, fresh = false): Observable<T> {
    if (fresh) {
      this.inFlightRequests.delete(requestKey);
    }
    const existing = this.inFlightRequests.get(requestKey) as Observable<T> | undefined;
    if (existing) {
      return existing;
    }

    const request = defer(requestFactory).pipe(
      finalize(() => {
        // Keep the completed read briefly so a grid host recreated while the
        // route settles reuses it. An explicit refresh passes `fresh` and
        // starts a new POST.
        window.setTimeout(() => {
          if (this.inFlightRequests.get(requestKey) === request) {
            this.inFlightRequests.delete(requestKey);
          }
        }, 1000);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.inFlightRequests.set(requestKey, request);
    return request;
  }
}
